import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";

export default function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (!current || !next) return toast.error("Fill both fields");
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use the new one next time you sign in.");
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="btn-change-password" variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white h-10">
          <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.5} /> Change password
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Change admin password</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Current password</Label>
            <Input data-testid="input-current-password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="velo-input mt-2 h-11" autoComplete="current-password" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">New password</Label>
            <Input data-testid="input-new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="velo-input mt-2 h-11" autoComplete="new-password" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Confirm new password</Label>
            <Input data-testid="input-confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="velo-input mt-2 h-11" autoComplete="new-password" />
          </div>
          <p className="text-xs text-slate-500">Minimum 6 characters. You'll need the new password on next sign-in.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); }} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5">Cancel</Button>
          <Button data-testid="btn-submit-change-password" onClick={submit} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
            {busy ? "Updating…" : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
